import { X } from 'lucide-react';
import { createContext, useCallback, useContext, useRef, useState, type ReactNode } from 'react';

interface Toast {
  id: number;
  tone: 'success' | 'danger' | 'info' | 'warning';
  text: string;
  action?: { label: string; onClick: () => void };
}

type Push = (t: Omit<Toast, 'id'>) => void;
const ToastContext = createContext<Push>(() => {});

/** Short status messages bottom-centre, with an optional action such as "Urungkan". */
export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const next = useRef(1);
  const dismiss = useCallback((id: number) => setToasts((ts) => ts.filter((t) => t.id !== id)), []);
  const push = useCallback<Push>(
    (t) => {
      const id = next.current++;
      setToasts((ts) => [...ts.slice(-2), { ...t, id }]);
      setTimeout(() => dismiss(id), t.action ? 8000 : 5000);
    },
    [dismiss],
  );

  return (
    <ToastContext.Provider value={push}>
      {children}
      <div
        aria-live="polite"
        style={{
          position: 'fixed',
          left: '50%',
          bottom: 20,
          transform: 'translateX(-50%)',
          zIndex: 80,
          display: 'flex',
          flexDirection: 'column',
          gap: 8,
          width: 'min(440px, calc(100% - 32px))',
        }}
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`fn-alert fn-alert--${t.tone}`}
            role="status"
            style={{ boxShadow: 'var(--shadow-popover)', background: 'var(--bg-surface)' }}
          >
            <div className="fn-alert__body" style={{ color: 'var(--text-primary)' }}>
              {t.text}
            </div>
            {t.action && (
              <button
                type="button"
                className="fn-btn fn-btn--ghost fn-btn--sm"
                onClick={() => {
                  t.action!.onClick();
                  dismiss(t.id);
                }}
              >
                {t.action.label}
              </button>
            )}
            <button
              type="button"
              className="fn-icon-btn fn-icon-btn--ghost fn-icon-btn--sm"
              aria-label="Tutup"
              onClick={() => dismiss(t.id)}
            >
              <X className="i i--sm" aria-hidden />
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export const useToast = () => useContext(ToastContext);
